import { FC } from 'react'

import { Box, Text } from '@factorialco/deprecated-design-system'
import { InternalExpenseViewerFieldsFragment } from 'generated/resources/graphql'

import { ExpenseFileViewer } from 'modules/expenses/components/ExpenseFileViewer'
import RecommendationSystem from 'modules/expenses/components/expenses/Detail/_RecommendationSystem'

import * as styles from './index.vanilla.css'

interface Props {
  expensable: InternalExpenseViewerFieldsFragment
}

const formatAmount = (amount: number | null | undefined, currency: string | null | undefined) => {
  if (amount == null) return '-'
  const formatted = (amount / 100).toFixed(2)
  return currency ? `${formatted} ${currency}` : formatted
}

const formatDate = (date: unknown) => {
  if (!date) return '-'
  return new Date(String(date)).toLocaleDateString()
}

const InfoRow: FC<{ label: string; value: string | number | null | undefined }> = ({
  label,
  value,
}) => (
  <Box
    flexDirection="row"
    paddingY="s8"
    style={{ borderBottom: '1px solid var(--border-default)' }}
  >
    <Box style={{ minWidth: '140px' }}>
      <Text.Normal size="200" color="grey600">
        {label}
      </Text.Normal>
    </Box>
    <Box style={{ flex: '1' }}>
      <Text.Normal size="200">{value ?? '-'}</Text.Normal>
    </Box>
  </Box>
)

const SectionTitle: FC<{ children: React.ReactNode }> = ({ children }) => (
  <Box paddingY="s12">
    <Text.Normal weight="bold">{children}</Text.Normal>
  </Box>
)

const InternalExpenseDetail: FC<Props> = ({ expensable }) => {
  const preview = expensable.preview
  const files = expensable.expense?.files ?? []
  const signedDocumentFileId = expensable.expense?.signedDocument?.file?.id

  const isSignedFile = (fileId: number) => {
    return signedDocumentFileId === fileId
  }

  return (
    <Box flexDirection="row" height="full">
      {/* Left panel: File viewer */}
      <Box
        style={{ flex: '1' }}
        background="grey200"
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        padding="s16"
        className={styles.fileViewerContainer}
      >
        {preview && preview.url && preview.contentType ? (
          <Box className={styles.fileViewer}>
            <ExpenseFileViewer url={preview.url} contentType={preview.contentType} />
          </Box>
        ) : (
          <Text.Normal color="grey600">No preview available</Text.Normal>
        )}
      </Box>

      {/* Right panel: Expense details */}
      <Box
        style={{ width: '400px' }}
        padding="s16"
        flexDirection="column"
        className={styles.detailsPanel}
      >
        <SectionTitle>Basic Information</SectionTitle>
        <InfoRow label="Amount" value={formatAmount(expensable.amount, expensable.currency)} />
        <InfoRow label="Date" value={formatDate(expensable.effectiveOn)} />
        <InfoRow label="Description" value={expensable.description} />
        <InfoRow label="Merchant" value={expensable.expense?.merchantName} />
        <InfoRow label="Category" value={expensable.expense?.categoryName} />
        <InfoRow label="Payment Method" value={expensable.expense?.paymentMethod} />

        <SectionTitle>Files ({files.length})</SectionTitle>
        {files.length > 0 ? (
          files.map((file) => (
            <Box
              key={file.id}
              paddingY="s8"
              style={{ borderBottom: '1px solid var(--border-default)' }}
              flexDirection="row"
              alignItems="center"
              gap="s8"
            >
              <Box style={{ flex: '1' }}>
                <Text.Normal size="200">{file.filename}</Text.Normal>
                {file.byteSize && (
                  <Text.Normal size="200" color="grey600">
                    {' '}
                    ({Math.round(file.byteSize / 1024)} KB)
                  </Text.Normal>
                )}
              </Box>
              {isSignedFile(file.id) && (
                <Box
                  paddingX="s8"
                  paddingY="s4"
                  background="moodPositive"
                  borderRadius={{
                    topLeft: 'abs004',
                    topRight: 'abs004',
                    bottomLeft: 'abs004',
                    bottomRight: 'abs004',
                  }}
                >
                  <Text.Normal size="100" color="white">
                    Signed
                  </Text.Normal>
                </Box>
              )}
            </Box>
          ))
        ) : (
          <Text.Normal size="200" color="grey600">
            No files attached
          </Text.Normal>
        )}

        {/* AI Recommendation System */}
        {expensable.expenseId && (
          <Box paddingTop="s16">
            <SectionTitle>AI Policy Check</SectionTitle>
            <RecommendationSystem key={expensable.expenseId} expenseId={expensable.expenseId} />
          </Box>
        )}
      </Box>
    </Box>
  )
}

export default InternalExpenseDetail
